'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_INTEGER,
  GTM_NOTSET_STRING,
  getGtmAttr,
  getScreenName,
} from '@musinsa/frontend-utils';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { CurationSnapDetailGoodsItem } from '@musinsa/services-curator';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { PropsWithStyles } from '../../../common';
import { useNavigate } from '../../../common/hooks/useNavigate';
import { useProductLinkNavigation } from '../../../product/hooks';
import { curatorQueries } from '../../../query-factory';
import { SnapTaggedGoodsCard } from './SnapTaggedGoodsCard';
import { SnapTaggedGoodsRowSheet } from './SnapTaggedGoodsRowSheet';

interface Props {
  goods: CurationSnapDetailGoodsItem[];
  curationId: string;
  snapId: string;
  pageSize: number;
  /** 큐레이터 샵 slug (amplitude curatorId 용) */
  shopSlug: string;
}

export type SnapTaggedGoodsRowContainerProps = PropsWithStyles<Props>;

export const SnapTaggedGoodsRowContainer: FC<PropsWithStyles<SnapTaggedGoodsRowContainerProps>> = (
  props,
) => {
  const { goods, curationId, snapId, pageSize, shopSlug, className, ...rest } = props;

  const queryClient = useQueryClient();
  const { navigate } = useNavigate();
  const navigateToProduct = useProductLinkNavigation();
  const { isMobile, service } = useDeviceInfo();

  const invalidateSnapDetail = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: curatorQueries.curations.snapDetail({ curationId, snapId, size: pageSize })
        .queryKey,
    });
  }, [curationId, pageSize, queryClient, snapId]);

  const sortedGoods = useMemo(
    () => [...goods].sort((a, b) => Number(a.isSoldOut) - Number(b.isSoldOut)),
    [goods],
  );

  if (sortedGoods.length === 0) {
    return <div className={clsx('h-8 w-full', className)} {...rest} />;
  }

  const isSingleItem = sortedGoods.length === 1;

  return (
    <div className="pb-[24px]!">
      <div
        className={clsx(
          'inline-flex w-full gap-0 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden relative bg-neutral-color-white',
          className,
        )}
        {...rest}
      >
        {sortedGoods.map((good, index) => {
          const isFirstItem = index === 0;
          const isLastItem = index === sortedGoods.length - 1;
          const displayRate = good.finalDiscount ?? good.couponSalesRate ?? good.salesRate ?? 0;
          const displayPrice = good.finalPrice ?? good.couponPrice ?? good.price;
          const { className: gtmClassName, ...gtmDataAttrs } =
            service === SERVICE.MUSINSA
              ? getGtmAttr(['select_item', 'view_item_list'], {
                  discount:
                    good.normalPrice != null && good.price != null
                      ? good.normalPrice - good.price
                      : GTM_NOTSET_INTEGER,
                  discount_rate: good.salesRate?.toString() ?? GTM_NOTSET_STRING,
                  item_brand: good.brandName ?? GTM_NOTSET_STRING,
                  item_category: GTM_NOTSET_STRING,
                  item_id: String(good.goodsNo),
                  item_list_id: GTM_NOTSET_STRING,
                  item_list_index: String(index),
                  original_price: good.normalPrice ?? GTM_NOTSET_INTEGER,
                  price: good.price ?? GTM_NOTSET_INTEGER,
                  quantity: 1,
                  ...(good.couponPrice != null ? { best_price: good.couponPrice } : {}),
                })
              : { className: undefined };

          return (
            <SnapTaggedGoodsCard.Root
              key={`${good.goodsNo}-${index}-${good.isLiked}`}
              className={clsx(
                isFirstItem && !isSingleItem && 'pl-[16px]!',
                isLastItem && !isSingleItem && 'pr-[16px]!',
                isSingleItem && 'w-full',
                gtmClassName,
              )}
              size="s"
              {...gtmDataAttrs}
              onClick={() => {
                if (service === SERVICE.MUSINSA) {
                  amplitudeSendEvent({
                    action_name: 'click',
                    component_name: 'product',
                    event_name: 'click_product',
                    from: getScreenName(),
                    id: shopSlug,
                    index,
                    section_name: 'curator_curationfeed',
                    product_id: String(good.goodsNo),
                    product_name: good.goodsName,
                    brand_name: good.brandName,
                    price: good.price,
                    discount_rate: good.salesRate,
                  });
                }
                navigateToProduct(good.linkUrl);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigateToProduct(good.linkUrl);
                }
              }}
            >
              <SnapTaggedGoodsCard.Image
                goodsNo={good.goodsNo}
                imageUrl={good.imageUrl}
                initialIsLiked={good.isLiked}
                invalidateSnapDetail={invalidateSnapDetail}
                isMobile={isMobile}
                isSoldOut={good.isSoldOut}
                navigate={navigate}
              />
              <SnapTaggedGoodsCard.Info
                className={clsx(!isSingleItem && 'max-w-[160px]!')}
                brandName={good.brandName}
                displayPrice={displayPrice}
                displayRate={displayRate}
                goodsName={good.goodsName}
                isExclusive={good.isExclusive === true}
              />
            </SnapTaggedGoodsCard.Root>
          );
        })}
        {!isSingleItem && (
          <SnapTaggedGoodsRowSheet
            goodsItems={sortedGoods}
            invalidateSnapDetail={invalidateSnapDetail}
          />
        )}
      </div>
    </div>
  );
};
